import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Input } from "reactstrap"
import {
    CleanHtml,
    FieldsetFormGroup,
    merchantSelectors,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    sharedConfigurationSelectors,
    SpinnerContainer,
    TextButton,
    useSelector,
} from "swiipe.portal.shared"
import LegalTextLinkPopup from "../../../components/legal/LegalTextLinkPopup"
import CancelService from "../../../components/merchant/CancelService"
import GoBackContainer from "../../../components/navigations/GoBackContainer"
import OnboardAnydayConnect from "../../../components/servicesonboarding/OnboardAnydayConnect"
import { SharedCancelContainer } from "../../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../../services/imageLanguageService"
import { removeSchemeFromHostName } from "../../../services/merchantService"
import { useRequiredUserRelations } from "../../../services/userRelationService"
import { StoreState } from "../../../store/StoreState"
import { gatewaySelectors, IGatewayDto } from "../../../store/reducers/gatewayReducer"
import {
    anydayDisableOnboardingThunk,
    getAnydayOnboardingDataThunk,
    IAnydayGetOnboardingDataResponse,
} from "../../../store/thunks/anydayThunks"
import { getGatewaysForMerchantThunk } from "../../../store/thunks/gatewayThunks"
import { sendServiceCancellationEmailThunk } from "../../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../../store/useReduxDispatch"
import "./AnydayOnboardingPage.scss"

interface IAnydayOnboardingPageProps {
    webshopIds?: string[]
    isEdit?: boolean
    onGoBack: () => void
    onOnboarded: () => void
}

interface IAnydayOnboardingPageInnerProps extends IAnydayOnboardingPageProps {
    merchantDetails: MixedMerchantDetails
    selectedWebshopId: string
}

const AnydayOnboardingPage = (props: IAnydayOnboardingPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }])
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId =
        props.webshopIds && props.webshopIds?.length > 0 ? props.webshopIds[0] : useSelector(merchantSelectors.selectedWebshopId)

    if (!merchantDetails || !merchantDetails.webshops.length || !selectedWebshopId) {
        return null
    }

    return <AnydayOnboardingPageInner merchantDetails={merchantDetails} selectedWebshopId={selectedWebshopId} {...props} />
}

const AnydayOnboardingPageInner = ({
    merchantDetails,
    selectedWebshopId,
    isEdit,
    onGoBack,
    onOnboarded,
}: IAnydayOnboardingPageInnerProps) => {
    const dispatch = useReduxDispatch()
    const { t } = useTranslation()
    const env = useSelector(sharedConfigurationSelectors.environment)

    const [onboardingData, setOnboardingData] = useState<IAnydayGetOnboardingDataResponse | undefined>()
    const [showCancelService, setShowCancelService] = useState(false)

    const webshopName = removeSchemeFromHostName(
        merchantDetails.webshops.find((w) => w.webshopId == selectedWebshopId)?.hostName || ""
    )

    useEffect(() => {
        if (isEdit && selectedWebshopId) {
            ;(async () => {
                const onboardingData = await dispatch(getAnydayOnboardingDataThunk(selectedWebshopId))

                setOnboardingData(onboardingData)
            })()
        }
    }, [isEdit, selectedWebshopId])

    const merchantId = merchantDetails.swMerchant.swMerchantId
    useEffect(() => {
        dispatch(getGatewaysForMerchantThunk(merchantId, false))
    }, [merchantId])

    const gateway = useSelector<StoreState, IGatewayDto | undefined>((state) =>
        gatewaySelectors.getGateway(state, merchantId, "Anyday", selectedWebshopId)
    )

    return (
        <>
            {showCancelService && gateway && (
                <CancelService
                    serviceName={t("anydayOnboarding.anyday")}
                    cancelText={t("cancelService.anyday.cancelText", { webshopName })}
                    customCancelledText={t("cancelService.anyday.cancelledText")}
                    onGoBack={() => setShowCancelService(false)}
                    onCancel={async () => {
                        await dispatch(anydayDisableOnboardingThunk(merchantId, selectedWebshopId, gateway.gatewayId))

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantId,
                                "AnydayCancellationEmail",
                                t("serviceNames.anyday"),
                                true
                            )
                        )
                    }}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit} disableDefaultPaddingBottom>
                    <div className="anyday-onboarding-container">
                        <div className="anyday-form-head">
                            <img className="anyday-logo" src={getImage("anydayLogo")} />
                        </div>

                        {!isEdit && (
                            <>
                                <OnboardAnydayConnect
                                    onOnboarded={onOnboarded}
                                    onGoBack={onGoBack}
                                    webshopIds={[selectedWebshopId]}
                                    merchantDetails={merchantDetails}
                                />
                                <TextButton
                                    text={t("commononboardingtexts.back")}
                                    className={cn(isEdit ? "mt-5" : "mt-4", "mb-4")}
                                    onClick={async () => onGoBack()}
                                />
                            </>
                        )}

                        {isEdit && (
                            <div className="anyday-edit-container">
                                <p className="edit-mode-text mt-5 mb-4 text-center">{t("anydayOnboarding.enabledNote")}</p>
                                <div className="anyday-edit-form my-5">
                                    <SpinnerContainer showSpinner={!onboardingData}>
                                        <FieldsetFormGroup field>
                                            <CleanHtml
                                                element="span"
                                                className="edit-mode-text purple"
                                                html={t(
                                                    env === "Production"
                                                        ? "anydayOnboarding.merchantApiKeyProd"
                                                        : env === "Betatest"
                                                        ? "anydayOnboarding.merchantApiKey"
                                                        : "anydayOnboarding.merchantApiKeyTest"
                                                )}
                                            />
                                            <Input name="apiKey" disabled defaultValue={onboardingData?.apiKey} />
                                        </FieldsetFormGroup>
                                        <FieldsetFormGroup field>
                                            <CleanHtml
                                                element="span"
                                                className="edit-mode-text purple"
                                                html={t("anydayOnboarding.merchantPrivateKey")}
                                            />
                                            <Input name="privateKey" disabled defaultValue={onboardingData?.privateKey} />
                                        </FieldsetFormGroup>
                                    </SpinnerContainer>

                                    <LegalTextLinkPopup
                                        legalTextBeforeLink=""
                                        legalTextAfterLink=""
                                        linkText={t("plugins.onboardedDescription.aeraTerms") + t("serviceNames.anyday")}
                                        termsType={"TermsAnyday"}
                                        className="mt-5 checkbox-text"
                                        centered
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </PageContainer>
                {isEdit && (
                    <SharedCancelContainer
                        cancelText={t("anydayOnboarding.cancel.buttonText", { webshopName: webshopName })}
                        onCancel={() => setShowCancelService(true)}
                    />
                )}
            </PageWrapper>
        </>
    )
}

export default AnydayOnboardingPage
